import React, {useEffect, useMemo, useState} from "react";
import {FormProvider, useFieldArray, useForm} from "react-hook-form";
import {useLocation, useNavigate} from "react-router-dom";
import PropTypes from "prop-types";
import styled from "styled-components";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";

// Hooks
import useApi from "../../hooks/useApi.js";
import usePrevious from "../../hooks/usePrevious.js";

// Utils
import {exists} from "../../utils/helpers.js";

// Components
import Modal from "../../components/Modal.js";
import ContactReorderPreview from "./ContactReorderPreview.js";
import SearchSelect from "../../components/SearchSelect.js";
import {InputSelect, InputError} from "../../components/form/FormInputs.js";

// Style
import {flex} from "../../style/components/mixins.js";
import {pad} from "../../style/components/variables.js";
import {
  Button,
  Form,
  ButtonLoader,
  Text,
  FormField,
  Inline,
  Small
} from "../../style/components/general.js";

const schema = yup.object().shape({
  details: yup.object().shape({
    contacts: yup.array().test({
      message: "At least one email is required.",
      test: arr => Array.isArray(arr) && arr?.length > 0
    })
  })
});

const ModalEditContacts = ({visible, setVisible, facility, setFacility}) => {
  const navigate = useNavigate();
  const {pathname} = useLocation();

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);

  const {api} = useApi("facilities");
  const {api: apiUsers} = useApi("facility-users");

  const getContactDepartments = curr => {
    const list = [];
    const added = [];
    curr?.map(({department}) => {
      if (department?.name && !added.includes(department.name)) {
        added.push(department.name);
        list.push({key: department.id, name: department.name});
      } else if (!department?.name && !added.includes("Contact")) {
        added.push("Contact");
        list.push({key: 0, name: "Contact"});
      }
    });
    return list;
  };

  const getContactCompanies = (curr, currDepartment) => {
    const list = [];
    const added = [];
    curr?.map(({company, department}) => {
      if ((department?.name || "Contact") === currDepartment)
        if (company?.name && !added.includes(company.name)) {
          added.push(company.name);
          list.push({key: company.id, name: company.name});
        } else if (!company?.name && !added.includes("No Company")) {
          added.push("No Company");
          list.push({key: 0, name: "No Company"});
        }
    });
    return list;
  };

  const form = useForm({
    defaultValues: {
      order: "Department",
      ...facility,
      details: {
        departments: getContactDepartments(facility.details.contactsExtra),
        companies: [],
        contacts: facility.details.contactsExtra
      }
    },
    resolver: yupResolver(schema)
  });
  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: {errors}
  } = form;
  const watchOrder = watch("order");
  const watchDepartment = watch("department");
  const watchCompany = watch("company");
  const watchContacts = watch("details.contacts");

  const {
    insert: insertDepartment,
    remove: removeDepartment
  } = useFieldArray({control, name: "details.departments"});

  const {
    insert: insertCompany,
    remove: removeCompany
  } = useFieldArray({
    control,
    name: "details.companies"
  });

  const {fields: contacts, remove, insert} = useFieldArray({control, name: "details.contacts"});

  const handleSearch = query => {
    const filter = {HasDepartment: true};

    if (query) filter.Search = query;

    apiUsers
      .callGet("", {
        facilityId: facility.type ? facility.id : null,
        limit: 20,
        filter: JSON.stringify(filter)
      })
      .then(({status, data}) => {
        if (status === 200 && data)
          setUsers(data.filter(({email}) => !contacts.some(contact => contact.email === email)));
      });
  };

  const prevOrder = usePrevious(watchOrder);
  const prevDepartment = usePrevious(watchDepartment);

  useEffect(() => {
    const departmentList = getContactDepartments(contacts);
    const departmentNames = departmentList.map(({name}) => name);
    const companiesList = watchDepartment ? getContactCompanies(contacts, watchDepartment) : [];
    const companyNames = companiesList.map(({name}) => name);

    if (prevOrder !== watchOrder && departmentNames[0]) setValue("department", departmentNames[0]);
    if (prevOrder !== watchOrder || prevDepartment !== watchDepartment)
      setValue("company", companyNames[0]);
    setValue("details.departments", departmentList);
    setAvailableDepartments(departmentNames);
    setValue("details.companies", companiesList);
    setAvailableCompanies(companyNames);
  }, [watchOrder, contacts, setValue, prevOrder, watchDepartment, prevDepartment]);

  const availableUsers = useMemo(() => {
    const list = [];

    contacts?.map(({id, department, company}) => {
      if (
        !list?.includes(id) &&
        (department?.name || "Contact") === watchDepartment &&
        (company?.name || "No Company") === watchCompany
      )
        list.push(id);
    });

    return list;
  }, [contacts, watchCompany, watchDepartment]);

  const reorderOptions = useMemo(() => {
    const companiesByDepartment = new Map();
    const contactsByCompany = new Map();

    contacts.forEach(contact => {
      const department = contact.department?.name || "Contact";
      const company = contact.company?.name || "No Company";
      if (!companiesByDepartment.has(department)) companiesByDepartment.set(department, new Set());
      companiesByDepartment.get(department).add(company);

      const companyKey = `${department}-${company}`;
      contactsByCompany.set(companyKey, (contactsByCompany.get(companyKey) || 0) + 1);
    });

    const canReorderDepartments = companiesByDepartment.size > 1;
    const canReorderCompanies = [...companiesByDepartment.values()].some(
      companies => companies.size > 1
    );
    const canReorderUsers = [...contactsByCompany.values()].some(count => count > 1);

    return [
      {
        label: canReorderDepartments
          ? "Order by Department"
          : "Order by Department (only one department is available)",
        value: "Department",
        disabled: !canReorderDepartments
      },
      {
        label: canReorderCompanies
          ? "Order by Company within Department"
          : "Order by Company within Department (no department has multiple companies)",
        value: "Company",
        disabled: !canReorderCompanies
      },
      {
        label: canReorderUsers
          ? "Order by User within Company"
          : "Order by User within Company (no company has multiple contacts)",
        value: "User",
        disabled: !canReorderUsers
      }
    ];
  }, [contacts]);

  useEffect(() => {
    const selectedOption = reorderOptions.find(option => option.value === watchOrder);
    const firstAvailableOption = reorderOptions.find(option => !option.disabled);
    if (selectedOption?.disabled && firstAvailableOption)
      setValue("order", firstAvailableOption.value);
  }, [reorderOptions, setValue, watchOrder]);

  const reorderByDepartment = result => {
    const {destination, source} = result;

    if (!destination || source.index === destination.index) return;

    const target = watch(`details.departments.${source.index}`);
    let sourceOffset = 0;
    let destinationOffset = 1;
    if (destination.index < source.index) {
      sourceOffset = 1;
      destinationOffset = 0;
    }
    insertDepartment(destination.index + destinationOffset, {...target});
    removeDepartment(source.index + sourceOffset);

    const reordered = [];
    watch("details.departments")?.map(department => {
      contacts.map(contact => {
        if (department.name === contact?.department?.name) reordered.push(contact);
        if (department.name === "Contact" && !contact?.department?.name) reordered.push(contact);
      });
    });
    setValue("details.contacts", reordered);
  };

  const reorderByCompany = result => {
    const {destination, source} = result;

    if (!destination || source.index === destination.index) return;

    // Reorder Companies
    const target = watch(`details.companies.${source.index}`);
    let sourceOffset = 0;
    let destinationOffset = 1;
    if (destination.index < source.index) {
      sourceOffset = 1;
      destinationOffset = 0;
    }

    let minIndex = null;
    let maxIndex = null;

    for (let i = 0; i < contacts.length; i++) {
      const curr = contacts[i];
      const {department} = curr;
      if ((department?.name || "Contact") === watchDepartment) {
        if (!exists(minIndex)) minIndex = i;
        maxIndex = i;
      }
    }

    const left = contacts.slice(0, minIndex);
    const middle = contacts.slice(minIndex, maxIndex + 1);
    const right = contacts.slice(maxIndex + 1, contacts.length);

    insertCompany(destination.index + destinationOffset, {...target});
    removeCompany(source.index + sourceOffset);

    const reordered = [];
    watch("details.companies")?.map(company => {
      middle.map(contact => {
        if (company.name === contact.company?.name) reordered.push(contact);
        if (company.name === "No Company" && !contact.company?.name) reordered.push(contact);
      });
    });
    setValue("details.contacts", [...left, ...reordered, ...right]);
  };

  const reorderByUser = result => {
    const {destination, source} = result;

    if (!destination || source.index === destination.index) return;

    let minIndex = null;
    let maxIndex = null;

    for (let i = 0; i < contacts.length; i++) {
      const curr = contacts[i];
      const {department, company} = curr;
      if (
        (department?.name || "Contact") === watchDepartment &&
        (company?.name || "No Company") === watchCompany
      ) {
        if (!exists(minIndex)) minIndex = i;
        maxIndex = i;
      }
    }

    const sourceIndex = contacts.findIndex(contact => contact.email === result.draggableId);
    if (sourceIndex < 0) return;

    const target = watch(`details.contacts.${sourceIndex}`);
    let sourceOffset = 0;
    let destinationOffset = 1;
    if (destination.index < source.index) {
      sourceOffset = 1;
      destinationOffset = 0;
    }

    let destinationIndex = minIndex + destination.index;
    if (exists(maxIndex) && destinationIndex > maxIndex) destinationIndex = maxIndex;
    if (exists(minIndex) && destinationIndex < minIndex) destinationIndex = minIndex;

    insert(destinationIndex + destinationOffset, {...target});
    remove(sourceIndex + sourceOffset);
  };

  const editFacility = ({details}) => {
    setLoading(true);

    const contactList = [];
    const contactSortObj = {};
    if (contacts?.length > 0) {
      contacts?.map(contact => {
        const company = contact.company?.name || "No Company";
        const department = contact?.department?.name || "Contact";

        if (!contactSortObj[department]) contactSortObj[department] = {[company]: [contact]};
        else if (!contactSortObj[department][company])
          contactSortObj[department][company] = [contact];
        else contactSortObj[department][company].push(contact);
      });

      Object.keys(contactSortObj).map(department => {
        const departmentCompanies = contactSortObj[department];
        Object.keys(departmentCompanies).map(company => {
          const companyContacts = departmentCompanies[company];
          companyContacts.map(contact => {
            contactList.push(contact.email);
          });
        });
      });
    }

    delete details.companies;
    delete details.departments;

    api
      .callPut(facility.id, {details: {contacts: contactList}})
      .then(({status, data}) => {
        if (status === 200 && data) {
          const updated = data.facility;
          setFacility(prev => ({
            ...prev,
            details: updated.details
          }));

          window.history.pushState({}, "Facility", updated.slug);

          // when changing facility name, need to update corresponding paths in facility dashboard tabs
          const matchedPathName = pathname.match(/^\/.+\/.+\/(.+)\/?/);
          const tabPathName = matchedPathName ? `/${matchedPathName[1]}` : "";
          navigate(`/facilities/${updated.slug}${tabPathName}`);
        }
      })
      .finally(() => {
        setLoading(false);
        setVisible(false);
      });
  };

  let onPreviewDragEnd = reorderByUser;
  if (watchOrder === "Department") onPreviewDragEnd = reorderByDepartment;
  else if (watchOrder === "Company") onPreviewDragEnd = reorderByCompany;

  const reorderDisabled =
    (watchOrder === "Department" && availableDepartments.length <= 1) ||
    (watchOrder === "Company" && availableCompanies.length <= 1) ||
    (watchOrder === "User" && availableUsers.length <= 1);

  let reorderEmptyMessage;
  if (watchOrder === "Department" && availableDepartments.length <= 1)
    reorderEmptyMessage = "Only one department is available, so there is nothing to reorder.";
  else if (watchOrder === "Company" && availableCompanies.length <= 1)
    reorderEmptyMessage = "This department has only one company, so there is nothing to reorder.";
  else if (watchOrder === "User" && availableUsers.length <= 1)
    reorderEmptyMessage = "This company has only one contact, so there is nothing to reorder.";

  return (
    facility && (
      <Modal visible={visible} setVisible={setVisible}>
        <FormProvider {...form}>
          <Form noValidate>
            <Wrapper>
              <Left>
                <FormField id="contacts">
                  <SearchSelect
                    label="Contact"
                    help="Show facility contacts. Contacts do not have system access. Their contact information is stored as a reference for specific sites and can be managed by Creator users on the User Management page."
                    search={handleSearch}
                    results={users}
                    setResults={setUsers}
                    placeholder="Add contact..."
                    add={contact => {
                      if (!contacts || !contacts.map(({email}) => email).includes(contact.email)) {
                        let lastOfCategory = null;

                        for (let i = 0; i < contacts.length; i++) {
                          const curr = contacts[i];
                          const {department, company} = curr;
                          if (
                            department?.name === contact.department?.name &&
                            company?.name === contact.company?.name
                          )
                            lastOfCategory = i;
                        }

                        insert(
                          exists(lastOfCategory) ? lastOfCategory + 1 : contacts.length,
                          contact
                        );
                      }
                    }}
                    showAll
                  />
                </FormField>
                <InputError errors={errors} name="details.contacts" />

                <OrderOptions>
                  {watchContacts?.length > 1 && (
                    <div>
                      <FormField>
                        <InputSelect
                          name="order"
                          label="Choose reorder target"
                          help="Choose the hierarchy level to reorder. Disabled choices do not have enough contact groups to reorder."
                          options={reorderOptions}
                          showAll
                        />
                      </FormField>

                      {(watchOrder === "Company" || watchOrder === "User") &&
                        availableDepartments.length > 1 && (
                          <FormField>
                            <InputSelect
                              name="department"
                              label="Choose Department"
                              help="Choose which department's companies or contacts you're reordering. Other departments stay put."
                              options={availableDepartments}
                              showAll
                            />
                          </FormField>
                        )}

                      {watchOrder === "User" && availableCompanies.length > 1 && (
                        <FormField>
                          <InputSelect
                            name="company"
                            label="Choose Company"
                            help="Choose which company's contacts you're reordering within the selected department."
                            options={availableCompanies}
                            showAll
                          />
                        </FormField>
                      )}

                      {watchOrder === "Company" &&
                        watchDepartment &&
                        availableCompanies.length <= 1 && (
                          <Text quiet>{reorderEmptyMessage}</Text>
                        )}

                      {watchOrder === "User" &&
                        watchDepartment &&
                        watchCompany &&
                        availableUsers.length <= 1 && (
                          <Text quiet>{reorderEmptyMessage}</Text>
                        )}

                      {watchOrder === "Department" && availableDepartments.length <= 1 && (
                        <Text quiet>{reorderEmptyMessage}</Text>
                      )}
                    </div>
                  )}
                </OrderOptions>

                <Submit
                  type="button"
                  onClick={handleSubmit(editFacility)}
                  loading={loading ? 1 : 0}>
                  Save {loading && <ButtonLoader />}
                </Submit>
              </Left>

              <Right>
                <PreviewDescription>
                  Preview Facility Contact Display: Drag to reorder the selected level.
                </PreviewDescription>
                <ContactsWrapper>
                  <ContactReorderPreview
                    contacts={watchContacts || []}
                    onRemove={email => {
                      const index = contacts.findIndex(contact => contact.email === email);
                      if (index >= 0) remove(index);
                    }}
                    reorder={{
                      type: watchOrder,
                      department: watchDepartment,
                      company: watchCompany,
                      disabled: reorderDisabled,
                      emptyMessage: reorderEmptyMessage,
                      onDragEnd: onPreviewDragEnd
                    }}
                  />
                </ContactsWrapper>
              </Right>
            </Wrapper>
          </Form>
        </FormProvider>
      </Modal>
    )
  );
};

ModalEditContacts.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  facility: PropTypes.objectOf(PropTypes.any).isRequired,
  setFacility: PropTypes.func.isRequired
};

// Style Overrides
const Wrapper = styled.div`
  position: relative;
  width: 100%;
  ${flex("row", "nowrap", "space-between", "start")};
`;

const Left = styled.div`
  width: 40%;
`;

const Right = styled.div`
  width: 60%;
`;

const OrderOptions = styled(Inline)`
  align-items: start;
  justify-content: start;
  width: 100%;
  gap: ${pad * 2}px;
`;

const PreviewDescription = styled(Small)`
  margin: ${pad / 2}px;
`;

const ContactsWrapper = styled.div`
  margin-top: ${pad/2}px;
  width: 100%;
  > div {
    min-width: 200px;
  }
`;

const Submit = styled(Button)`
  margin: ${pad}px 0;
`;

export default ModalEditContacts;
